import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "inputs" }
const _hoisted_2 = { class: "inputs__dropdown" }
const _hoisted_3 = { class: "inputs__dropdown" }
const _hoisted_4 = {
  key: 0,
  class: "inputs__dropdown return-container"
}
const _hoisted_5 = { class: "inputs__dropdown" }
const _hoisted_6 = { class: "date-pickers-container" }
const _hoisted_7 = {
  key: 0,
  class: "date-pickers-container"
}
const _hoisted_8 = { class: "inputs__dropdown" }
const _hoisted_9 = { class: "button-submit" }

import SvgIcon from "@/components/base/SvgIcon.vue";
import useDialog from "@/composables/useDialog";
import { dateObject } from "@/helpers/dateHelpers";
import {
    createRequest,
    getDatesPlaceholder,
    getDefaultReturn,
    getNextValidDate,
    getReturnRoutes,
    isValidPassengers,
} from "@/helpers/quickformHelpers";
import { Direction } from "@/models/Enums";
import { DialogType } from "@/models/symbols/Dialog";
import { Filter } from "@/modules/AvailabilityCalendar/models/AvailabilityFilter";
import { routes } from "@/modules/Quickform/config/routes";
import { usePassengerStore } from "@/store/passengerStore";
import { useQuickFormStore } from "@/store/quickFormStore";
import { useVehicleStore } from "@/store/vehicleStore";
import useVuelidate from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import { onClickOutside } from "@vueuse/core";
import { AxiosError } from "axios";
import { VBtn, VDatePicker, VSelect } from "v-ferry-components";
import { computed, reactive, ref, watch } from "vue";
import useAvailableDates from "../composables/useAvailableDates";
import useCreateRequest from "../composables/useCreateRequest";
import { AvailableDates } from "../models/AvailableDates";
import { DepartureRequest, DepartureResponse } from "../models/QuickformApi";
import { Passenger, QuickformState } from "../models/QuickformStates";
import Passengers from "./passengers/PassengersForm.vue";
import VehicleForm from "./vehicle/VehicleForm.vue";

interface Props {
    hasReturn?: boolean;
    booking?: QuickformState;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'QuickformBaseForm',
  props: {
    hasReturn: { type: Boolean },
    booking: {}
  },
  setup(__props: any) {

const props = __props;

const { getDates } = useAvailableDates();

const passengerStore = usePassengerStore();

const vehicleStore = useVehicleStore();

const quickFormStore = useQuickFormStore();

const outwardDates = ref<AvailableDates>();

const returnDates = ref<AvailableDates>();

const returnRoutes = ref<Filter[]>([]);

const showDates = ref(false);

const dropdownDate = ref();

const initialPage = (outward?: boolean) => {
    const date = outward ? state.outwardDate : state.returnDate ? state.returnDate : state.outwardDate;

    return dateObject(date ? new Date(date) : new Date());
};

onClickOutside(dropdownDate, () => (showDates.value = false));

const getReturnRoute = () => {
    if (props.booking?.outwardRoute) {
        returnRoutes.value = getReturnRoutes(props.booking.outwardRoute);
        getDates(props.booking.outwardRoute).then((dates) => {
            outwardDates.value = dates;
        });
        getDates(props.booking.returnRoute).then((dates) => {
            returnDates.value = dates;
        });
    }
    return props.booking?.returnRoute ?? "";
};

const state = reactive<QuickformState>({
    passengers: {
        passengers: passengerStore.getPassengers(Direction.Outward),
        returnPassengers: passengerStore.getPassengers(Direction.Return),
    } as Passenger,
    outwardRoute: props.booking?.outwardRoute ?? "",
    returnRoute: getReturnRoute(),
    outwardDate: props.booking?.outwardDate ?? undefined,
    returnDate: props.booking?.returnDate ?? undefined,
});

const rules = {
    passengers: {
        required: helpers.withMessage("Need to have at least one adult on each journey", () => {
            return isValidPassengers(props.hasReturn);
        }),
    },
    outwardRoute: { required },
    returnRoute: {
        required: requiredIf(function () {
            return props.hasReturn;
        }),
    },
    outwardDate: { required },
    returnDate: {
        required: requiredIf(function () {
            return props.hasReturn;
        }),
    },
};

const v$ = useVuelidate(rules, state);

const datesPlaceholder = computed(() => getDatesPlaceholder(state.outwardDate, state.returnDate, props.hasReturn));

watch(
    () => state.outwardRoute,
    (current) => {
        if (current) {
            setReturnOptions(current);
        }
    },
);

const setReturnOptions = (outwardRoute: string, isSwitch?: boolean) => {
    returnRoutes.value = getReturnRoutes(outwardRoute);
    getDates(outwardRoute).then((dates) => {
        outwardDates.value = dates;
    });
    state.outwardDate = undefined;
    if (!isSwitch) {
        const defaultReturn = getDefaultReturn(outwardRoute);
        state.returnRoute = defaultReturn ? defaultReturn : returnRoutes.value[0].value;
        onReturnRouteSelect(state.returnRoute);
    }
};

const onReturnRouteSelect = (returnRoute: string) => {
    state.returnDate = undefined;
    getDates(returnRoute).then((dates) => {
        returnDates.value = dates;
    });
};

const switchRoutes = () => {
    if (!state.returnRoute) return;
    const outward = state.outwardRoute;
    state.outwardRoute = state.returnRoute;
    setReturnOptions(state.outwardRoute, true);
    state.returnRoute = outward;
    onReturnRouteSelect(state.returnRoute);
};

watch(
    () => state.outwardDate,
    (current) => {
        if (!current) return;
        if (!state.returnDate || current > state.returnDate) {
            state.returnDate = getNextValidDate(current, returnDates.value?.disabledDates);
        }
    },
);

const { create, isLoading } = useCreateRequest();

const { displayDialog } = useDialog();

const search = async () => {
    const isValid = await v$.value.$validate();

    if (!isValid) return;

    const vehiclesOutward = vehicleStore.getVehiclesOfRoute(Direction.Outward);
    const vehiclesReturn = vehicleStore.getVehiclesOfRoute(Direction.Return);

    const passengersOutward = passengerStore.getPassengersForApi(Direction.Outward);
    const passengersReturn = passengerStore.getPassengersForApi(Direction.Return);

    const request: DepartureRequest[] = [];

    request.push(createRequest(state, passengersOutward, vehiclesOutward));

    if (props.hasReturn) {
        request.push(
            createRequest(
                state,
                passengerStore.getDifferentDetails ? passengersReturn : passengersOutward,
                vehicleStore.getDifferentDetails ? vehiclesReturn : vehiclesOutward,
                props.hasReturn,
            ),
        );
    }

    create(request)
        .then((data) => {
            quickFormStore.setDepartures(request);
            window.location.href = "/book/departures";
        })
        .catch((err: AxiosError) => {
            const errResponse: DepartureResponse = err.response?.data as DepartureResponse;
            console.error("Error creating departure request", JSON.stringify(errResponse));
            if (errResponse.error) {
                //bokkit error code : 12033 length on vehicle too short
                displayDialog({
                    title: errResponse.message ?? "Something went wrong",
                    message:
                        errResponse.bookitErrorCode == 12009
                            ? "Sorry - you cannot book more vehicles than the number of travellers."
                            : "Something unexpected happened, please try again",
                    confirmText: "I understand",
                    type: DialogType.danger,
                });
            } else {
                quickFormStore.setDepartures(request);
                window.location.href = "/book/departures";
            }
        });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    class: "round-trip",
    onSubmit: _withModifiers(search, ["prevent"])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _cache[5] || (_cache[5] = _createElementVNode("label", null, "Passenger", -1)),
        _createVNode(Passengers, {
          errorMessage: _unref(v$).passengers.$errors.map((e) => e.$message.toString())[0],
          hasReturn: _ctx.hasReturn
        }, null, 8, ["errorMessage", "hasReturn"])
      ]),
      _createElementVNode("div", _hoisted_3, [
        _cache[6] || (_cache[6] = _createElementVNode("label", null, "Outward Route", -1)),
        _createVNode(_unref(VSelect), {
          class: _normalizeClass(["outward-route", { 'outward-route-return': _ctx.hasReturn }]),
          placeholder: "Select route",
          modelValue: _unref(v$).outwardRoute.$model,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).outwardRoute.$model) = $event)),
          options: _unref(routes),
          hasError: _unref(v$).outwardRoute.$error,
          onSelect: setReturnOptions,
          clearable: false
        }, null, 8, ["class", "modelValue", "options", "hasError"])
      ]),
      (_ctx.hasReturn)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", {
              onClick: switchRoutes,
              class: "switch-icon"
            }),
            _cache[7] || (_cache[7] = _createElementVNode("label", null, "Return Route", -1)),
            _createVNode(_unref(VSelect), {
              class: "return-route",
              placeholder: "Return route",
              options: returnRoutes.value,
              modelValue: _unref(v$).returnRoute.$model,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).returnRoute.$model) = $event)),
              hasError: _unref(v$).returnRoute.$error,
              onSelect: onReturnRouteSelect,
              clearable: false
            }, null, 8, ["options", "modelValue", "hasError"])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_5, [
        _cache[10] || (_cache[10] = _createElementVNode("label", null, "Dates", -1)),
        _createVNode(_unref(VBtn), {
          onClick: _cache[2] || (_cache[2] = ($event: any) => (showDates.value = !showDates.value)),
          class: _normalizeClass(["dropdown-btn", { hasError: _unref(v$).outwardDate.$error || _unref(v$).returnDate.$error }]),
          color: "ghost",
          type: "button"
        }, {
          default: _withCtx(() => [
            _createElementVNode("span", null, _toDisplayString(datesPlaceholder.value), 1),
            _createVNode(SvgIcon, {
              class: _normalizeClass(["icon", { open: showDates.value }]),
              icon: "carbon/chevron-down"
            }, null, 8, ["class"])
          ]),
          _: 1
        }, 8, ["class"]),
        (showDates.value)
          ? (_openBlock(), _createElementBlock("div", {
              ref_key: "dropdownDate",
              ref: dropdownDate,
              class: _normalizeClass([{ 'no-return': !props.hasReturn }, "date-pickers dates-dropdown"]),
              key: JSON.stringify(initialPage())
            }, [
              _createElementVNode("div", _hoisted_6, [
                _cache[8] || (_cache[8] = _createElementVNode("h3", null, "Outward date:", -1)),
                _createVNode(_unref(VDatePicker), {
                  isInline: "",
                  class: "date-picker",
                  modelValue: _unref(v$).outwardDate.$model,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).outwardDate.$model) = $event)),
                  "min-date": new Date(),
                  "max-date": outwardDates.value?.endDate,
                  disabledDates: outwardDates.value?.disabledDates,
                  disable: !state.outwardRoute.length,
                  weekdaysFormat: "W",
                  "initial-page": initialPage(true)
                }, null, 8, ["modelValue", "min-date", "max-date", "disabledDates", "disable", "initial-page"])
              ]),
              (_ctx.hasReturn)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _cache[9] || (_cache[9] = _createElementVNode("h3", null, "Return date:", -1)),
                    _createVNode(_unref(VDatePicker), {
                      isInline: "",
                      class: "date-picker",
                      modelValue: _unref(v$).returnDate.$model,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(v$).returnDate.$model) = $event)),
                      "min-date": _unref(v$).outwardDate.$model ?? new Date(),
                      "max-date": returnDates.value?.endDate,
                      disabledDates: returnDates.value?.disabledDates,
                      disable: !state.returnRoute.length,
                      weekdaysFormat: "W",
                      "initial-page": initialPage()
                    }, null, 8, ["modelValue", "min-date", "max-date", "disabledDates", "disable", "initial-page"])
                  ]))
                : _createCommentVNode("", true)
            ], 2))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_8, [
        _cache[11] || (_cache[11] = _createElementVNode("label", null, "Vehicle", -1)),
        _createVNode(VehicleForm, { hasReturn: _ctx.hasReturn }, null, 8, ["hasReturn"])
      ])
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createVNode(_unref(VBtn), { isLoading: _unref(isLoading) }, {
        default: _withCtx(() => _cache[12] || (_cache[12] = [
          _createTextVNode("Book now")
        ])),
        _: 1,
        __: [12]
      }, 8, ["isLoading"])
    ])
  ], 32))
}
}

})